/**
 * Logic hook for SignatoryInlineDropdown — extracted per the "custom hooks for component logic"
 * convention (docs/conventions/custom-hooks.md) so the component file stays composition + JSX.
 * Pattern reference: useComposeEmailLogic.tsx (colocated use<Component>Logic hook).
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MagicSignRow, MagicSignStatus, Signatory, SignatoryUser, ConfigMeta } from '../../../types';
import { fetchMagicSignUsers } from '../../../MagicSignService';
import { useAppSelector } from '../../../../../app/hooks';
import { MAX_SIGNATORIES_DEFAULT } from '../../../constants';

// Minimal user shape the section renderer needs (company users, PNO users and the selected list
// are all normalised to this).
export type SectionUser = { userId: string; name: string; email: string };

interface UseSignatoryInlineDropdownOptions {
    row: MagicSignRow;
    configMeta: ConfigMeta | null;
    onSave: (id: string, signatories: Signatory[]) => void;
}

const useSignatoryInlineDropdown = ({ row, configMeta, onSave }: UseSignatoryInlineDropdownOptions) => {
    const platformData = useAppSelector((state) => state.user.domainMetadata);
    const pnoId: string = platformData?.investorOrgId ?? '';
    const [searchParams] = useSearchParams();
    // The row carries its own orgId (company context) — the query param only exists on
    // deep-linked views, so it is just a fallback
    const orgId: string = row.orgId ?? searchParams.get('orgId') ?? '';

    const maxSignatories: number = configMeta?.maxSignatories ?? MAX_SIGNATORIES_DEFAULT;
    const sigs = row.signatories ?? [];
    // Editable only while UPLOADED. Once links are generated (any later status) the dropdown still
    // OPENS so the user can SEE who is signing, but every checkbox is disabled (read-only).
    const isLocked = row.status !== MagicSignStatus.UPLOADED;

    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [companyUsers, setCompanyUsers] = useState<SignatoryUser[]>([]);
    const [pnoUsers, setPnoUsers] = useState<SignatoryUser[]>([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [selected, setSelected] = useState<Signatory[]>(sigs);
    const containerRef = useRef<HTMLDivElement>(null);
    const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Sync local selected list whenever the row's signatories change externally
    useEffect(() => {
        setSelected(row.signatories ?? []);
    }, [row.signatories]);

    // Clear the pending debounced search on unmount so a row closed mid-debounce can't fire
    // loadUsers against an unmounted component.
    useEffect(() => {
        return () => {
            if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        };
    }, []);

    const loadUsers = useCallback(
        async (q: string) => {
            if (!pnoId) return;
            setUsersLoading(true);
            // Load company users and admin (internal PNO) users in parallel — each rendered as its
            // own section in the dropdown. A failure in either list is non-fatal (that section is empty).
            const companyP = orgId
                ? fetchMagicSignUsers({ pnoId, orgId, search: q })
                      .then((res) => res?.data?.data ?? [])
                      .catch((error) => {
                          console.error('[SignatoryInlineDropdown] Failed to load company users', error);
                          return [] as SignatoryUser[];
                      })
                : Promise.resolve([] as SignatoryUser[]);
            const pnoP = fetchMagicSignUsers({ pnoId, search: q })
                .then((res) => res?.data?.data ?? [])
                .catch((error) => {
                    console.error('[SignatoryInlineDropdown] Failed to load PNO users', error);
                    return [] as SignatoryUser[];
                });
            try {
                const [company, pno] = await Promise.all([companyP, pnoP]);
                setCompanyUsers(company);
                setPnoUsers(pno);
            } finally {
                setUsersLoading(false);
            }
        },
        [pnoId, orgId],
    );

    const handleOpen = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            setOpen(true);
            setSearch('');
            loadUsers('');
        },
        [loadUsers],
    );

    const handleSearchChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const val = e.target.value;
            setSearch(val);
            if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
            searchTimerRef.current = setTimeout(() => loadUsers(val), 300);
        },
        [loadUsers],
    );

    const isChecked = useCallback((user: SectionUser) => selected.some((s) => s.email === user.email), [selected]);

    const atCap = selected.length >= maxSignatories;

    const handleToggle = useCallback(
        (user: SectionUser) => {
            if (isLocked) return;
            if (isChecked(user)) {
                const next = selected.filter((s) => s.email !== user.email);
                setSelected(next);
                onSave(row.id, next);
            } else {
                if (atCap) return;
                const next: Signatory[] = [...selected, { userId: user.userId, name: user.name, email: user.email }];
                setSelected(next);
                onSave(row.id, next);
            }
        },
        [isLocked, isChecked, selected, atCap, onSave, row.id],
    );

    // Close panel when clicking outside
    useEffect(() => {
        if (!open) return;
        const handleOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, [open]);

    return {
        open,
        search,
        companyUsers,
        pnoUsers,
        usersLoading,
        selected,
        containerRef,
        maxSignatories,
        isLocked,
        atCap,
        handleOpen,
        handleSearchChange,
        isChecked,
        handleToggle,
    };
};

export default useSignatoryInlineDropdown;
